import { useQuery } from '@tanstack/react-query';
import { MajorsResult } from '@common-types';
import { getMajors } from '@api/majors';
import useMajorSearchStore from '@store/MajorSearchStore';
import useStateSpotlightStore, { selectIsWaitingForDefaults } from '@store/StateSpotlightStore';

interface UseMajorsReturn {
  data: MajorsResult | undefined;
  refetch: () => Promise<unknown>;
  isLoading: boolean;
}

export const useMajors = ({
  waitForStateDefaults = true,
}: { waitForStateDefaults?: boolean } = {}): UseMajorsReturn => {
  const filters = useMajorSearchStore((state) => state.filters);
  const sortByValues = useMajorSearchStore((state) => state.sortByValues);
  const isSortSelected = useMajorSearchStore((state) => state.isSortSelected);
  const useStateSorts = useStateSpotlightStore((state) => !!state.spotlight);
  const isWaitingForDefaults = useStateSpotlightStore(selectIsWaitingForDefaults('majors')) && waitForStateDefaults;

  const query = useQuery<MajorsResult>({
    queryKey: ['majors', filters, sortByValues, isSortSelected, useStateSorts],
    queryFn: () => getMajors({ filters, sortByValues, isSortSelected, useStateSorts }),
    enabled: !isWaitingForDefaults && Object.keys(filters).length > 0,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  return {
    data: query.data,
    refetch: () => query.refetch(),
    isLoading: query.isLoading || isWaitingForDefaults,
  };
};
